<script setup lang="ts">
import { UIButtonLink } from '@groover-dev/groover-ui'
import { storeToRefs } from 'pinia'

import GroovizAmount from '~/components/ui/GroovizAmount.vue'

import { useSegmentTrack } from '~/composables/Segment/useSegmentTrack'
import { useSegmentTrackCampaign } from '~/composables/useSegmentTrackCampaign'

import { useUserStore } from '~/stores/user'

const segmentTrack = useSegmentTrack()
const { trackCampaignInitiated } = useSegmentTrackCampaign()
const { HAS_PAID: USER_HAS_PAID } = storeToRefs(useUserStore())
const {
  is_eligible_for_groover_plus: IS_ELIGIBLE_FOR_GROOVER_PLUS,
  IS_GROOVER_PLUS_MEMBER,
  groover_plus_status: GROOVER_PLUS_STATUS,
} = storeToRefs(useUserAgencyStore())

const commonButtonProps = {
  theme: 'dark' as const,
  size: 'small' as const,
  color: 'neutral' as const,
  class: 'tw-whitespace-nowrap',
}

function trackArtistReferral() {
  segmentTrack('Referral Artist - Page Accessed', {
    accessed_from: 'top navbar',
  })
}

function emitDraftStart() {
  trackCampaignInitiated('header')
}
</script>

<template>
  <div>
    <UIButtonLink
      v-bind="commonButtonProps"
      to="/draft/"
      data-test-id="bandTopNavigationStartCampaignCTA"
      @click="emitDraftStart"
    >
      {{ $t('layout.navigation.button.startNewDraft.campaign') }}</UIButtonLink
    >
    <UIButtonLink
      v-bind="commonButtonProps"
      to="/band/dashboard/"
      data-test-id="bandTopNavigationCampaignsCTA"
    >
      {{ $t('layout.navigation04') }}
    </UIButtonLink>
    <UIButtonLink
      v-bind="commonButtonProps"
      to="/lp/our-services/"
      data-test-id="bandTopNavigationServicesCTA"
      class="tw-flex tw-h-[41px] tw-items-center tw-gap-x-xs"
    >
      {{ $t('layout.navbar.artist.servicesLink') }}
    </UIButtonLink>
    <UIButtonLink
      v-if="USER_HAS_PAID"
      v-bind="commonButtonProps"
      to="/band/referral/"
      data-test-id="bandTopNavigationReferralCTA"
      @click="trackArtistReferral"
    >
      <i18n-t
        tag="span"
        class="tw-inline-flex tw-flex-nowrap tw-items-center tw-gap-x-2xs"
        keypath="layout.navbar.artist.referralLink"
      >
        <template #amount><GroovizAmount amount="10" /></template>
      </i18n-t>
    </UIButtonLink>
    <UIButtonLink
      v-if="IS_GROOVER_PLUS_MEMBER"
      v-bind="{ ...commonButtonProps, color: 'accent' }"
      :to="`/plans/groover-plus-${GROOVER_PLUS_STATUS}ly/`"
      data-test-id="bandTopNavigationGrooverPlusCTA"
      >{{ $t('layout.navbar.artist.planBenefitsLink') }}</UIButtonLink
    >
    <UIButtonLink
      v-else-if="IS_ELIGIBLE_FOR_GROOVER_PLUS"
      v-bind="{ ...commonButtonProps, color: 'accent' }"
      to="/plans/"
      data-test-id="bandTopNavigationGrooverPlusCTA"
      >{{ $t('layout.navbar.artist.plansLink') }}</UIButtonLink
    >
  </div>
</template>

<style lang="scss" scoped>
.dashMenu {
  top: calc(100% + 20px);
}

.icon {
  @apply tw-mx-3 tw-h-4;

  @screen lg {
    @apply tw-mx-4;
  }

  @screen xl {
    @apply tw-mx-6;
  }
}

.fas.fa-gift {
  @apply tw-cursor-pointer tw-text-lg tw-text-icon-inverse;

  @keyframes wiggleWaggle {
    from {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(15deg);
    }
    75% {
      transform: rotate(-15deg);
    }
    to {
      transform: rotate(0deg);
    }
  }

  @media (prefers-reduced-motion: no-preference) {
    animation: 100ms ease-in-out 4 1500ms reverse both running wiggleWaggle;
  }
}
</style>
